import { useEffect, useRef, useState } from 'react'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { onlineManager, useQueryClient } from '@tanstack/react-query'
import { CloudOff, Fingerprint, Loader2, LogIn, MailQuestion } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { AUTH_KEYS, seedSession, useLogin, usePasskeyLogin, useRequestPasswordReset } from '@/features/auth/hooks'
import { fetchMe } from '@/features/auth/api'
import { landingRouteFor } from '@/features/auth/guards'
import { completeSSOLogin, startSSOLogin } from '@/features/sso/api'
import { useSSOStatus } from '@/features/sso/hooks'
import { GoogleIcon } from '@/features/sso/google-icon'
import { useOnline } from '@/hooks/useOnline'
import type { SessionUser } from '@/types/auth'
import wavesLogo from '@/assets/waves.svg'

export const Route = createFileRoute('/login')({
  beforeLoad: async ({ context }) => {
    // Already signed in? Skip the login screen. Custom roles land on their
    // first allowed flow — the dashboard is admin-only. Offline, /auth/me is
    // unreachable but a restored session is good enough to bounce back in.
    const me = await context.queryClient
      .fetchQuery({ queryKey: AUTH_KEYS.me, queryFn: fetchMe })
      .catch(() => null)
    const fallback =
      !onlineManager.isOnline() ? context.queryClient.getQueryData<SessionUser | null>(AUTH_KEYS.me) : null
    const session = me ?? fallback
    if (session) throw redirect({ to: landingRouteFor(session) as never })
  },
  component: LoginPage,
})

function LoginPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const login = useLogin()
  const passkeyLogin = usePasskeyLogin()
  const requestReset = useRequestPasswordReset()
  // Public status probe: the Google button only shows when the account
  // enabled Google sign-in.
  const ssoStatus = useSSOStatus()
  const [mode, setMode] = useState<'signin' | 'forgot'>('signin')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [resetSent, setResetSent] = useState(false)
  const [ssoBusy, setSsoBusy] = useState(false)
  // Google redirects back with ?error=... when the user cancels; derived once
  // at mount (no effect needed).
  const [ssoCancelled] = useState(
    () => new URLSearchParams(window.location.search).has('error'),
  )
  // A 401 redirect (lib/api.ts) sends the user here with ?expired=1 plus the
  // page they were on, so they can be returned to it after signing in.
  const [loginParams] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    const returnTo = params.get('returnTo') ?? ''
    return {
      expired: params.has('expired'),
      // Only same-origin app paths — never an absolute or protocol-relative URL.
      returnTo: returnTo.startsWith('/') && !returnTo.startsWith('//') ? returnTo : '',
    }
  })
  // StrictMode remounts effects in dev; the state is single-use, so complete
  // the callback at most once.
  const ssoCompleting = useRef(false)

  const busy = login.isPending || passkeyLogin.isPending
  const online = useOnline()
  // None of the sign-in methods can run without a connection (the session
  // cookie only exists server-side) — say so instead of failing cryptically.
  const signable = online && !busy

  // Custom roles land on their first allowed flow; admins on the dashboard.
  // A session-expiry redirect returns the user to the page they were on.
  const onSuccess = (user: SessionUser) =>
    navigate({ to: (loginParams.returnTo || landingRouteFor(user)) as never })

  // Google redirects back to /login?code=...&state=... (a cancellation comes
  // back as ?error=... and is surfaced via ssoCancelled). Finish the ceremony
  // here, then navigate. State changes happen only in the async callbacks —
  // the page navigates away on success, so no busy flag is needed meanwhile.
  useEffect(() => {
    if (ssoCompleting.current) return
    const params = new URLSearchParams(window.location.search)
    const code = params.get('code')
    const state = params.get('state')
    if (!code || !state) return
    ssoCompleting.current = true
    // Drop the OAuth params from the address bar first so a refresh can't
    // replay the exchange.
    window.history.replaceState(null, '', window.location.pathname)
    completeSSOLogin(code, state)
      .then((user) => {
        seedSession(queryClient, user)
        navigate({ to: (loginParams.returnTo || landingRouteFor(user)) as never })
      })
      .catch((err: Error) => setError(err.message || 'Google sign-in failed'))
  }, [navigate, queryClient, loginParams.returnTo])

  const onGoogle = async () => {
    setError(null)
    setSsoBusy(true)
    try {
      const { url } = await startSSOLogin()
      window.location.href = url
    } catch (err) {
      setSsoBusy(false)
      setError(err instanceof Error ? err.message : 'Could not start Google sign-in')
    }
  }

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    login.mutate(
      { username, password },
      {
        onSuccess: onSuccess,
        onError: (err) => setError(err.message || 'Login failed'),
      },
    )
  }

  const onPasskey = () => {
    setError(null)
    passkeyLogin.mutate(undefined, {
      onSuccess: onSuccess,
      onError: (err) =>
        setError(
          err.message?.includes('passkey') || err.message?.includes('ceremony')
            ? err.message
            : 'Passkey sign-in failed or was canceled',
        ),
    })
  }

  const onForgot = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!username.trim()) {
      setError('Enter your username or email.')
      return
    }
    // Always shows the sent note — the backend never reveals whether the
    // login exists.
    requestReset.mutate(username.trim(), {
      onSuccess: () => setResetSent(true),
      onError: (err) => setError(err.message || 'Failed to send reset email'),
    })
  }

  return (
    <div className="bg-background flex min-h-svh items-center justify-center p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="items-center text-center">
          <img src={wavesLogo} alt="FlowPilot logo" className="mx-auto mb-2 h-14 w-14" />
          <CardTitle className="text-xl">
            {mode === 'signin' ? 'Sign in to FlowPilot' : 'Reset your password'}
          </CardTitle>
          <CardDescription>
            {mode === 'signin'
              ? 'Use your account username and password'
              : "Enter your account's username or email"}
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          {!online && (
            <p className="text-muted-foreground flex items-start gap-2 rounded-md border border-dashed px-3 py-2 text-sm">
              <CloudOff className="mt-0.5 h-4 w-4 shrink-0" />
              You're offline — signing in requires a connection.
            </p>
          )}
          {loginParams.expired && mode === 'signin' && online && (
            <p className="text-muted-foreground rounded-md border border-dashed px-3 py-2 text-sm">
              Your session expired — sign in to continue where you left off.
            </p>
          )}
          {mode === 'signin' ? (
            <>
              <form onSubmit={onSubmit} className="grid gap-3">
                <div className="grid gap-2">
                  <Label htmlFor="username">Username</Label>
                  <Input
                    id="username"
                    autoComplete="username webauthn"
                    autoCapitalize="none"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    disabled={busy}
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={busy}
                    required
                  />
                </div>
                {error && <p className="text-destructive text-sm">{error}</p>}
                {!error && ssoCancelled && (
                  <p className="text-destructive text-sm">Google sign-in was canceled</p>
                )}
                <Button type="submit" disabled={!signable}>
                  {login.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <LogIn className="mr-2 h-4 w-4" />
                  )}
                  Sign in
                </Button>
              </form>
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground justify-self-start text-sm underline-offset-4 hover:underline"
                onClick={() => {
                  setMode('forgot')
                  setError(null)
                  setResetSent(false)
                }}
              >
                Forgot password?
              </button>
              <div className="flex items-center gap-2 text-muted-foreground text-xs">
                <span className="h-px flex-1 bg-border" /> or{' '}
                <span className="h-px flex-1 bg-border" />
              </div>
              <Button variant="outline" onClick={onPasskey} disabled={!signable}>
                {passkeyLogin.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Fingerprint className="mr-2 h-4 w-4" />
                )}
                Sign in with a passkey
              </Button>
              {ssoStatus.data?.enabled && (
                <Button variant="outline" onClick={onGoogle} disabled={!signable || ssoBusy}>
                  {ssoBusy ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <GoogleIcon className="mr-2 h-4 w-4" />
                  )}
                  Sign in with Google
                </Button>
              )}
            </>
          ) : (
            <form onSubmit={onForgot} className="grid gap-3">
              {resetSent ? (
                <p className="text-muted-foreground text-sm">
                  If that account exists, a password reset link has been sent. Check your email and
                  follow the link within 30 minutes.
                </p>
              ) : (
                <>
                  <div className="grid gap-2">
                    <Label htmlFor="reset-login">Username or email</Label>
                    <Input
                      id="reset-login"
                      autoComplete="username"
                      autoCapitalize="none"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      disabled={requestReset.isPending}
                      required
                    />
                  </div>
                  {error && <p className="text-destructive text-sm">{error}</p>}
                  <Button type="submit" disabled={requestReset.isPending}>
                    {requestReset.isPending ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <MailQuestion className="mr-2 h-4 w-4" />
                    )}
                    Send reset link
                  </Button>
                </>
              )}
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground justify-self-start text-sm underline-offset-4 hover:underline"
                onClick={() => {
                  setMode('signin')
                  setError(null)
                  setResetSent(false)
                }}
              >
                ← Back to sign in
              </button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
